import { get } from 'svelte/store';

import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { user_data } from 'src/stores/account/user';
import { mockLightsGalleryEnabled } from 'src/stores/features/dev/mock-lights-gallery';
import type { Device } from 'src/types/Device';
import {
	MOCK_LIGHT_CATALOG,
	buildMockLightDevice,
	buildMockLightGroups,
	buildMockLightSettings,
	buildMockLightStatus,
} from './mock_light_catalog';
import { MOCK_LIGHT_GROUP_ID_LIST } from './mock_light_ids';

export { isMockLightDevice, isMockLightDeviceId } from './mock_light_ids';
export {
	MOCK_LIGHT_CATALOG,
	MOCK_LIGHT_COHORT_LABELS,
	getCatalogCoverageKeys,
	getFamilyLightModeKeys,
	getMockLightIdsByCohort,
} from './mock_light_catalog';

let injecting = false;
let lifecycleStarted = false;

const firstRoomId = (): number => {
	const roomList = Object.values(get(rooms) || {}) as Array<{ id?: number }>;
	const room = roomList.find((item) => item?.id != null);
	return room?.id ?? 0;
};

const mocksAlreadyPresent = (): boolean => {
	const currentDevices = get(devices);
	const currentGroups = get(groups);
	const devicesPresent = MOCK_LIGHT_CATALOG.every((entry) => Boolean(currentDevices[entry.id]));
	const groupsPresent = MOCK_LIGHT_GROUP_ID_LIST.every(
		(id) => Boolean(currentGroups[id]) || Boolean(currentGroups[String(id)]),
	);
	return devicesPresent && groupsPresent;
};

const statusMissing = (): boolean =>
	MOCK_LIGHT_CATALOG.some((entry) => !devices_status.getByRealId(entry.id));

export const ensureMockLightsInjected = (): void => {
	if (injecting) return;
	if (!get(mockLightsGalleryEnabled)) return;
	if (!get(user_data)?.email) return;
	if (mocksAlreadyPresent() && !statusMissing()) return;

	injecting = true;
	try {
		const roomId = firstRoomId();
		const mockDevices = MOCK_LIGHT_CATALOG.reduce<Record<string, Device>>((acc, entry, index) => {
			acc[entry.id] = buildMockLightDevice(entry, roomId, index);
			return acc;
		}, {});

		devices.massUpdate(mockDevices);

		const mockGroups = buildMockLightGroups(roomId);
		MOCK_LIGHT_GROUP_ID_LIST.forEach((id) => {
			groups.update(mockGroups[id]);
		});

		MOCK_LIGHT_CATALOG.forEach((entry) => {
			devices_status.update_device_status({
				id: entry.id,
				msg: buildMockLightStatus(entry),
			});
			devices_settings.update_devices_settings(entry.id, buildMockLightSettings(entry));
		});
	} finally {
		injecting = false;
	}
};

export const removeMockLights = (): void => {
	injecting = true;
	try {
		MOCK_LIGHT_CATALOG.forEach((entry) => {
			devices.remove(entry.id);
			devices_status.remove(entry.id);
			devices_settings.remove(entry.id);
		});
		MOCK_LIGHT_GROUP_ID_LIST.forEach((id) => {
			groups.remove(id);
		});
	} finally {
		injecting = false;
	}
};

export const startMockLightsGalleryLifecycle = (): void => {
	if (lifecycleStarted) return;
	lifecycleStarted = true;

	mockLightsGalleryEnabled.subscribe((enabled) => {
		if (enabled) ensureMockLightsInjected();
		else removeMockLights();
	});

	devices.subscribe(() => {
		if (injecting || !get(mockLightsGalleryEnabled) || !get(user_data)?.email) return;
		ensureMockLightsInjected();
	});

	groups.subscribe(() => {
		if (injecting || !get(mockLightsGalleryEnabled) || !get(user_data)?.email) return;
		ensureMockLightsInjected();
	});
};
